import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Grid, message } from "antd";
import { OrderListPage } from "../../orders/pages/OrderListPage";
import { orderApi } from "../../orders/api/orderApi";
import { ItemModel, OrdersMap } from "../../orders/model/orders.ui";

const { useBreakpoint } = Grid;
const PAGE_SIZE = 10;

interface OrderListCache {
    orders: ItemModel[];
    page: number;
    totalPages: number;
    hasMore: boolean;
    searchText: string;
    statusFilter: string;
    dateRange: [string, string] | null;
}

let cachedOrderList: OrderListCache | null = null;

export const OrderView: React.FC = () => {
    const location = useLocation();
    const shouldReload = (location.state as { reload?: boolean } | null)?.reload === true;

    const screens = useBreakpoint();
    const isDesktop = !!screens.md;

    const [orders, setOrders] = useState<ItemModel[]>(() => {
        if (!shouldReload && cachedOrderList) return cachedOrderList.orders;
        return [];
    });
    const [page, setPage] = useState<number>(() => {
        if (!shouldReload && cachedOrderList) return cachedOrderList.page;
        return 1;
    });
    const [totalPages, setTotalPages] = useState<number>(() => {
        if (!shouldReload && cachedOrderList) return cachedOrderList.totalPages;
        return 1;
    });
    const [hasMore, setHasMore] = useState<boolean>(() => {
        if (!shouldReload && cachedOrderList) return cachedOrderList.hasMore;
        return true;
    });
    const [isLoading, setIsLoading] = useState<boolean>(() => {
        if (!shouldReload && cachedOrderList) return false;
        return true;
    });
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

    const [searchText, setSearchText] = useState<string>(() => {
        if (!shouldReload && cachedOrderList) return cachedOrderList.searchText;
        return "";
    });
    const [statusFilter, setStatusFilter] = useState<string>(() => {
        if (!shouldReload && cachedOrderList) return cachedOrderList.statusFilter;
        return "all";
    });
    const [dateRange, setDateRange] = useState<[string, string] | null>(() => {
        if (!shouldReload && cachedOrderList) return cachedOrderList.dateRange;
        return null;
    });

    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const updateCache = useCallback((updated: Partial<OrderListCache>) => {
        cachedOrderList = {
            orders: updated.orders ?? cachedOrderList?.orders ?? [],
            page: updated.page ?? cachedOrderList?.page ?? 1,
            totalPages: updated.totalPages ?? cachedOrderList?.totalPages ?? 1,
            hasMore: updated.hasMore ?? cachedOrderList?.hasMore ?? true,
            searchText: updated.searchText ?? cachedOrderList?.searchText ?? "",
            statusFilter: updated.statusFilter ?? cachedOrderList?.statusFilter ?? "all",
            dateRange: updated.dateRange !== undefined ? updated.dateRange : (cachedOrderList?.dateRange ?? null),
        };
    }, []);

    const fetchOrders = useCallback(
        async (
            targetPage: number,
            isRefresh = false,
            searchParam = searchText,
            statusParam = statusFilter,
            dateRangeParam = dateRange,
            append = false
        ) => {
            if (isRefresh) {
                setIsRefreshing(true);
            } else if (append) {
                setIsLoadingMore(true);
            } else {
                setIsLoading(true);
            }

            try {
                const searchArg = searchParam.trim() || undefined;
                const statusArg = statusParam !== "all" ? statusParam : undefined;
                const createdFromArg = dateRangeParam && dateRangeParam[0] ? `${dateRangeParam[0]}T00:00:00` : undefined;
                let createdToArg = dateRangeParam && dateRangeParam[1] ? `${dateRangeParam[1]}T23:59:59` : undefined;
                if (createdToArg !== undefined && createdFromArg !== undefined && createdToArg <= createdFromArg) {
                    createdToArg = undefined;
                }

                const response = await orderApi.getListOrder(
                    targetPage,
                    PAGE_SIZE,
                    searchArg,
                    statusArg,
                    createdFromArg,
                    createdToArg
                );
                const mappedItems = OrdersMap(response?.items || []);
                const pages = response?.pagination?.total_pages ?? 1;
                const newHasMore = targetPage < pages && mappedItems.length > 0;

                setOrders((prev) => {
                    const newOrders = append ? [...prev, ...mappedItems] : mappedItems;
                    updateCache({
                        orders: newOrders,
                        page: targetPage,
                        totalPages: pages,
                        hasMore: newHasMore,
                        searchText: searchParam,
                        statusFilter: statusParam,
                        dateRange: dateRangeParam,
                    });
                    return newOrders;
                });
                setPage(targetPage);
                setTotalPages(pages);
                setHasMore(newHasMore);
            } catch (error) {
                console.error("Failed to fetch orders:", error);
            } finally {
                setIsRefreshing(false);
                setIsLoadingMore(false);
                setIsLoading(false);
            }
        },
        [searchText, statusFilter, dateRange, updateCache]
    );

    useEffect(() => {
        if (shouldReload || !cachedOrderList) {
            cachedOrderList = null;
            // Clear reload state in history so page refreshes don't re-trigger reload
            window.history.replaceState({}, document.title);
            fetchOrders(1, false, "", "all", null);
        }
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [shouldReload]);

    const handleSearchChange = (val: string) => {
        setSearchText(val);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            fetchOrders(1, false, val, statusFilter, dateRange);
        }, 350);
    };

    const handleStatusFilterChange = (val: string) => {
        setStatusFilter(val);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        fetchOrders(1, false, searchText, val, dateRange);
    };

    const handleDateRangeChange = (dates: [string, string] | null) => {
        setDateRange(dates);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        fetchOrders(1, false, searchText, statusFilter, dates);
    };

    // Handle Refresh
    const handleRefresh = async () => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        await fetchOrders(1, true, searchText, statusFilter, dateRange);
    };

    // Handle Load More (mobile) — append to existing list
    const handleLoadMore = async () => {
        if (isLoadingMore || isRefreshing || !hasMore) return;
        await fetchOrders(page + 1, false, searchText, statusFilter, dateRange, true);
    };

    // Handle Page Change (desktop pagination) — replace list
    const handlePageChange = async (newPage: number) => {
        if (newPage === page || newPage < 1 || newPage > totalPages) return;
        await fetchOrders(newPage, false, searchText, statusFilter, dateRange, false);
    };

    // Handle Delete Order
    const handleDeleteOrder = async (order: ItemModel) => {
        const orderId = order.order_id || order.id;
        if (!orderId) return;
        try {
            await orderApi.deleteOrder(orderId);
            message.success("Xóa đơn hàng thành công!");
            await fetchOrders(page, true, searchText, statusFilter, dateRange);
        } catch (error) {
            console.error("Failed to delete order:", error);
            message.error("Xóa đơn hàng thất bại!");
        }
    };

    return (
        <OrderListPage
            orders={orders}
            isDesktop={isDesktop}
            searchText={searchText}
            onSearchChange={handleSearchChange}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            onRefresh={handleRefresh}
            onLoadMore={handleLoadMore}
            onPageChange={handlePageChange}
            onDeleteOrder={handleDeleteOrder}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            currentPage={page}
            totalPages={totalPages}
        />
    );
};

export default OrderView;
